import { Card } from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HeaderSection } from "./HeaderSection";
import { InfoBanner } from "./InfoBanner";
import { InvoiceAllocationTable } from "./InvoiceAllocationTable";
import { OverpaymentCreditBanner } from "./OverpaymentCreditBanner";
import { ReceiptAmountSection } from "./ReceiptAmountSection";
import styles from "./ReceiptMultiInvoiceAllocation.module.scss";
import { ReceiptInvoiceAllocationRow, ReceiptMultiInvoiceAllocationProps } from "./types";
import { Invoice } from "../types";
import { buildInitialAllocationRows, distributeReceiptTotal, mapToAllocationRow } from "./utils";

export type { ReceiptInvoiceAllocationRow, ReceiptMultiInvoiceAllocationProps } from "./types";
export { mapToAllocationRow } from "./utils";

export const ReceiptMultiInvoiceAllocation: React.FC<ReceiptMultiInvoiceAllocationProps> = ({
  invoices: controlledInvoices,
  receiptTotalAmount: controlledReceiptTotal,
  availableOverpaymentCredit,
  tenantName,
  applyOverpaymentCredit: controlledApplyCredit = false,
  onReceiptTotalChange,
  onInvoicesChange,
  onApplyOverpaymentCreditChange,
  onAllocationValidityChange,
  leaseId,
  tenantId,
  pendingInvoicesSubtitle,
  onInvoiceClick,
  disabled = false,
}) => {
  const hasAvailableCredit = Number(availableOverpaymentCredit) > 0;
  const applyOverpaymentCredit = hasAvailableCredit && controlledApplyCredit;
  const [internalInvoices, setInternalInvoices] = useState<ReceiptInvoiceAllocationRow[]>(() =>
    controlledInvoices?.length
      ? buildInitialAllocationRows(controlledInvoices, controlledReceiptTotal ?? 0)
      : [],
  );
  const [internalReceiptTotal, setInternalReceiptTotal] = useState(controlledReceiptTotal ?? 0);
  const [invalidAllocationInputIds, setInvalidAllocationInputIds] = useState<Record<string, boolean>>({});
  const lastEmittedSignatureRef = useRef<string>("");
  const buildSignature = (rows: ReceiptInvoiceAllocationRow[] | any[]) =>
    (rows ?? [])
      .map((row) =>
        [
          row?.pdcId || row?.id || row?._id || "",
          row?.balance ?? "",
          row?.invoiceNo || row?.invoiceNumber || "",
        ].join(":"),
      )
      .join("|");

  useEffect(() => {
    if (applyOverpaymentCredit || controlledReceiptTotal === undefined) return;
    setInternalReceiptTotal(controlledReceiptTotal);
  }, [controlledReceiptTotal, applyOverpaymentCredit]);

  const controlledInvoicesSignature = useMemo(() => {
    if (!controlledInvoices?.length) return "";

    return controlledInvoices
      .map((row) =>
        [
          row?.pdcId || row?.id || row?._id || "",
          row?.balance ?? "",
          row?.invoiceNo || row?.invoiceNumber || "",
        ].join(":"),
      )
      .join("|");
  }, [controlledInvoices]);

useEffect(() => {
  if (controlledInvoices === undefined || !controlledInvoicesSignature) return;

  // Suppress the round-trip of our own internal updates (remove/add/select/edit),
  // so removing a row never auto-redistributes the remaining allocations.
  if (controlledInvoicesSignature === lastEmittedSignatureRef.current) return;

  const receiptTotal =
    applyOverpaymentCredit && hasAvailableCredit
      ? Number(availableOverpaymentCredit) || 0
      : (controlledReceiptTotal ?? 0);

  const nextInvoices = buildInitialAllocationRows(controlledInvoices, receiptTotal);

  if (nextInvoices.length) {
    setInternalInvoices(nextInvoices);
  }
  // Only re-sync on genuine external identity/balance changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [controlledInvoicesSignature, applyOverpaymentCredit, availableOverpaymentCredit, hasAvailableCredit]);

  const receiptTotalAmount = internalReceiptTotal;

  const invoices = internalInvoices;

  const updateInvoices = useCallback((nextInvoices: typeof invoices) => {
    setInternalInvoices(nextInvoices);
  }, []);

  useEffect(() => {
    onInvoicesChange?.(internalInvoices);
    lastEmittedSignatureRef.current = buildSignature(internalInvoices);
  }, [internalInvoices, onInvoicesChange]);

  const updateReceiptTotal = useCallback(
    (amount: number) => {
      setInternalReceiptTotal(amount);
      onReceiptTotalChange?.(amount);
    },
    [onReceiptTotalChange],
  );

  const selectedCount = useMemo(() => invoices.filter((invoice) => invoice.selected).length, [invoices]);

  const totalAllocated = useMemo(
    () =>
      invoices.reduce((sum, invoice) => {
        if (!invoice.selected) return sum;
        return sum + (Number(invoice.allocation) || 0);
      }, 0),
    [invoices],
  );

  const hasSelectedInvoices = selectedCount > 0;

  const overpaymentCredit = useMemo(() => {
    if (!hasSelectedInvoices) return 0;
    const excess = receiptTotalAmount - totalAllocated;
    return excess > 0 ? excess : 0;
  }, [receiptTotalAmount, totalAllocated, hasSelectedInvoices]);

  const allocationExceedsLimit = useMemo(() => {
    const limitCents = Math.round(receiptTotalAmount * 100);
    const allocatedCents = Math.round(totalAllocated * 100);
    return allocatedCents > limitCents;
  }, [receiptTotalAmount, totalAllocated]);

  const hasInvalidAllocationInput = Object.keys(invalidAllocationInputIds).length > 0;

  const isAllocationValid = hasSelectedInvoices && !allocationExceedsLimit && !hasInvalidAllocationInput;

  useEffect(() => {
    onAllocationValidityChange?.(isAllocationValid);
  }, [isAllocationValid, onAllocationValidityChange]);

  const handleApplyCreditChange = useCallback(
    (applied: boolean) => {
      onApplyOverpaymentCreditChange?.(applied);
    },
    [onApplyOverpaymentCreditChange],
  );

  useEffect(() => {
    if (!hasAvailableCredit) return;

    const nextTotal = applyOverpaymentCredit
      ? Number(availableOverpaymentCredit) || 0
      : Number(controlledReceiptTotal) || 0;

    setInternalReceiptTotal(nextTotal);
    onReceiptTotalChange?.(nextTotal);
    setInternalInvoices((currentInvoices) =>
      currentInvoices.length ? distributeReceiptTotal(nextTotal, currentInvoices) : currentInvoices,
    );
  }, [applyOverpaymentCredit]);

  const handleAutoDistribute = useCallback(
    (amount: number) => {
      updateReceiptTotal(amount);
      updateInvoices(distributeReceiptTotal(amount, invoices));
    },
    [invoices, updateInvoices, updateReceiptTotal],
  );

  const handleAllocationChange = useCallback(
    (id: string, value: number) => {
      const nextInvoices = invoices.map((invoice) => (invoice.id === id ? { ...invoice, allocation: value } : invoice));
      updateInvoices(nextInvoices);
    },
    [invoices, updateInvoices],
  );

  const handleAllocationInputValidityChange = useCallback((id: string, isValid: boolean) => {
    setInvalidAllocationInputIds((prev) => {
      if (isValid) {
        if (!(id in prev)) return prev;
        const { [id]: _, ...rest } = prev;
        return rest;
      }

      if (prev[id] === false) return prev;
      return { ...prev, [id]: false };
    });
  }, []);

  const handleRemoveInvoice = useCallback(
    (id: string) => {
      const nextInvoices = invoices.filter((invoice) => invoice.id !== id);
      
      // updateInvoices(distributeReceiptTotal(receiptTotalAmount, nextInvoices));
      updateInvoices(nextInvoices);
    },
    [invoices, updateInvoices],
  );

  const handleAddInvoice = useCallback(
    (invoice: Invoice) => {
      const exists = invoices.some((row) => row.id === invoice.pdcId);
      if (exists) return;

      const newRow = mapToAllocationRow(invoice, invoices.length);
      const nextInvoices = distributeReceiptTotal(receiptTotalAmount, [
        ...invoices,
        { ...newRow, selected: true, removable: true },
      ]);
      updateInvoices(nextInvoices);
    },
    [invoices, receiptTotalAmount, updateInvoices],
  );

  const handleSelectionChange = useCallback(
    (selectedRowKeys: React.Key[]) => {
      const mandatoryIds = invoices.filter((invoice) => invoice.removable === false).map((invoice) => invoice.id);
      const selectedIds = new Set([...selectedRowKeys.map(String), ...mandatoryIds]);
      const nextInvoices = distributeReceiptTotal(
        receiptTotalAmount,
        invoices.map((invoice) => ({
          ...invoice,
          selected: selectedIds.has(invoice.id),
        })),
      );
      updateInvoices(nextInvoices);
    },
    [invoices, receiptTotalAmount, updateInvoices],
  );

  return (
    <>
      {hasAvailableCredit && (
        <OverpaymentCreditBanner
          availableCredit={Number(availableOverpaymentCredit)}
          applyCredit={applyOverpaymentCredit}
          disabled={disabled}
          onApplyCreditChange={handleApplyCreditChange}
        />
      )}
      <Card bordered={false} className={styles.root} bodyStyle={{ padding: 0 }}>
        <HeaderSection />

        <div className={styles.body}>
          <ReceiptAmountSection
            receiptTotalAmount={receiptTotalAmount}
            applyOverpaymentCredit={applyOverpaymentCredit}
            tenantName={tenantName}
            disabled={disabled}
            onReceiptTotalChange={updateReceiptTotal}
            onAutoDistribute={handleAutoDistribute}
          />

          <InvoiceAllocationTable
            invoices={invoices}
            selectedCount={selectedCount}
            totalAllocated={totalAllocated}
            allocationLimit={receiptTotalAmount}
            allocationExceedsLimit={allocationExceedsLimit}
            hasSelectedInvoices={hasSelectedInvoices}
            overpaymentCredit={overpaymentCredit}
            applyOverpaymentCredit={applyOverpaymentCredit}
            disabled={disabled}
            leaseId={leaseId}
            tenantId={tenantId}
            pendingInvoicesSubtitle={pendingInvoicesSubtitle}
            onSelectionChange={handleSelectionChange}
            onAllocationChange={handleAllocationChange}
            onAllocationInputValidityChange={handleAllocationInputValidityChange}
            onRemove={handleRemoveInvoice}
            onAddInvoice={handleAddInvoice}
            onInvoiceClick={onInvoiceClick}
          />
        </div>
      </Card>
      <InfoBanner applyOverpaymentCredit={applyOverpaymentCredit} />
    </>
  );
};

export default ReceiptMultiInvoiceAllocation;
